/* eslint-disable react/react-in-jsx-scope */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Chip from '@material-ui/core/Chip';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import LinearProgress from '@material-ui/core/LinearProgress';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import SmartToyIcon from '@material-ui/icons/Android';
import { makeStyles } from '@material-ui/core/styles';
import { aiReadinessFixApiRef, type CheckMeta as ApiCheckMeta } from '../../api/aiReadinessFixApi';
import type {
  FileChangePreview,
  AgentCheckPreview,
  FixResultItem,
} from '../../api/aiReadinessFixApi';
import { FileChangeAccordion } from './FileChangeAccordion';

const PREVIEW_DEBOUNCE_MS = 400;
const DEFAULT_BRANCH = 'cerebro/ai-readiness';

const useStyles = makeStyles(theme => ({
  sectionLabel: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  agentItem: {
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  agentChip: {
    marginLeft: theme.spacing(1),
    height: 20,
    fontSize: '0.65rem',
  },
  prLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontSize: '0.85rem',
    '&:hover': { textDecoration: 'underline' },
  },
  resultRow: {
    marginBottom: theme.spacing(1.5),
  },
  inputField: {
    marginBottom: theme.spacing(2),
  },
  userInputSection: {
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
}));

/** Shape matching CheckResult from EntityAiReadinessContent */
export interface FixableCheck {
  checkId: string;
  checkName: string;
  remediationAction?: string;
  category?: string;
}

interface FixPreviewDialogProps {
  open: boolean;
  entityRef: string;
  checks: FixableCheck[];
  onClose: () => void;
}

// Annotation keys that require user input (no server-side resolution yet)
const USER_INPUT_ANNOTATION_KEYS: Record<string, string> = {
  hasPagerDutyServiceId: 'pagerduty.com/service-id',
  hasSnykTargetId: 'snyk.io/target-id',
  hasJiraProjectKey: 'jira/project-key',
};

const USER_INPUT_LABELS: Record<string, string> = {
  'pagerduty.com/service-id': 'PagerDuty Service ID (e.g. PE7J3MH)',
  'snyk.io/target-id': 'Snyk Target ID (e.g. UUID from Snyk, or "Chub-Engineering/repo-name" for all targets in a GitHub repo)',
  'jira/project-key': 'Jira Project Key(s) (e.g. DEVEX)',
};

export function FixPreviewDialog({
  open,
  entityRef,
  checks,
  onClose,
}: FixPreviewDialogProps) {
  const classes = useStyles();
  const api = useApi(aiReadinessFixApiRef);

  const [branchName, setBranchName] = useState(DEFAULT_BRANCH);
  const [annotationOverrides, setAnnotationOverrides] = useState<Record<string, string>>({});

  // Preview state
  const [previewFiles, setPreviewFiles] = useState<FileChangePreview[]>([]);
  const [agentChecks, setAgentChecks] = useState<AgentCheckPreview[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [hasPreviewed, setHasPreviewed] = useState(false);

  // Apply state
  const [applying, setApplying] = useState(false);
  const [results, setResults] = useState<FixResultItem[] | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const checkMetas: Record<string, ApiCheckMeta> = Object.fromEntries(
    checks.map(c => [c.checkId, { name: c.checkName, remediationAction: c.remediationAction }]),
  );
  const checkIds = checks.map(c => c.checkId);

  const runPreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await api.preview({
        entityRef,
        checkIds,
        checkMetas,
        annotationOverrides,
      });
      setPreviewFiles(res.files);
      setAgentChecks(res.agentChecks);
      setHasPreviewed(true);
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, entityRef, JSON.stringify(checkIds), JSON.stringify(annotationOverrides)]);

  // Auto-preview on open and when overrides change (debounced)
  useEffect(() => {
    if (!open) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      void runPreview();
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, runPreview]);

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setResults(null);
      setHasPreviewed(false);
      setPreviewFiles([]);
      setAgentChecks([]);
      setPreviewError(null);
      setBranchName(DEFAULT_BRANCH);
      setAnnotationOverrides({});
    }
  }, [open]);

  const handleApply = async () => {
    setApplying(true);
    try {
      const res = await api.apply({
        entityRef,
        checkIds,
        checkMetas,
        branchName: branchName.trim() || DEFAULT_BRANCH,
        annotationOverrides,
      });
      setResults(res.results);
    } catch (e) {
      setResults(
        checks.map(c => ({
          checkId: c.checkId,
          checkName: c.checkName,
          fixType: 'programmatic' as const,
          error: e instanceof Error ? e.message : String(e),
        })),
      );
    } finally {
      setApplying(false);
    }
  };

  const userInputChecks = checks.filter(c => USER_INPUT_ANNOTATION_KEYS[c.checkId]);
  const needsUserInput = userInputChecks.length > 0;

  const programmaticCount = checks.filter(
    c => c.remediationAction === 'add-annotation' || c.remediationAction === 'add-file',
  ).length;
  const agentCount = checks.filter(c => c.remediationAction === 'agent-fix').length;

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Fix AI Readiness Issues</DialogTitle>

      {results ? (
        // Results view
        <>
          <DialogContent>
            <Typography variant="subtitle2" className={classes.sectionLabel}>
              Results
            </Typography>
            {results.map(r => (
              <Box key={r.checkId} className={classes.resultRow}>
                <Box display="flex" alignItems="center">
                  <Typography variant="body2">
                    <strong>{r.checkName}</strong>
                  </Typography>
                  <Chip
                    label={r.fixType === 'agent' ? 'agent fix' : 'auto-fix'}
                    size="small"
                    color={r.fixType === 'programmatic' ? 'primary' : 'default'}
                    className={classes.agentChip}
                  />
                </Box>
                {r.error && (
                  <Typography variant="body2" color="error">
                    {r.error}
                  </Typography>
                )}
                {r.prUrl && (
                  <a href={r.prUrl} target="_blank" rel="noopener noreferrer" className={classes.prLink}>
                    View pull request <OpenInNewIcon style={{ fontSize: 14 }} />
                  </a>
                )}
                {r.workflowRunUrl && (
                  <a href={r.workflowRunUrl} target="_blank" rel="noopener noreferrer" className={classes.prLink}>
                    View agent workflow run <OpenInNewIcon style={{ fontSize: 14 }} />
                  </a>
                )}
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} color="primary">
              Close
            </Button>
          </DialogActions>
        </>
      ) : (
        // Preview + configure view
        <>
          <DialogContent>
            {/* Branch name */}
            <TextField
              className={classes.inputField}
              label="Branch name"
              fullWidth
              value={branchName}
              onChange={e => setBranchName(e.target.value)}
              helperText={`Default: ${DEFAULT_BRANCH}`}
              disabled={applying}
            />

            {/* User-input required annotations */}
            {needsUserInput && (
              <Box className={classes.userInputSection}>
                <Typography variant="subtitle2" gutterBottom>
                  Values required
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  The following checks need values that cannot be resolved automatically.
                  Enter them below; preview will update automatically.
                </Typography>
                {userInputChecks.map(c => {
                  const annotationKey = USER_INPUT_ANNOTATION_KEYS[c.checkId]!;
                  return (
                    <TextField
                      key={c.checkId}
                      className={classes.inputField}
                      label={USER_INPUT_LABELS[annotationKey] ?? annotationKey}
                      fullWidth
                      value={annotationOverrides[annotationKey] ?? ''}
                      onChange={e =>
                        setAnnotationOverrides(prev => ({
                          ...prev,
                          [annotationKey]: e.target.value,
                        }))
                      }
                      disabled={applying}
                    />
                  );
                })}
              </Box>
            )}

            {/* What will be fixed summary */}
            <Box display="flex" alignItems="center" mb={1} style={{ gap: 8 }}>
              {programmaticCount > 0 && (
                <Chip
                  label={`${programmaticCount} auto-fix${programmaticCount !== 1 ? 'es' : ''} → 1 PR`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
              {agentCount > 0 && (
                <Chip
                  icon={<SmartToyIcon style={{ fontSize: 14 }} />}
                  label={`${agentCount} agent fix${agentCount !== 1 ? 'es' : ''}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>

            <Divider style={{ marginBottom: 12 }} />

            {/* Preview section */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2">Preview</Typography>
              {previewLoading && <CircularProgress size={18} />}
            </Box>

            {previewError && (
              <Typography color="error" variant="body2" paragraph>
                {previewError}
              </Typography>
            )}

            {!previewLoading && hasPreviewed && previewFiles.length === 0 && agentChecks.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                No file changes to preview.
              </Typography>
            )}

            {/* File change accordions — collapsed by default per requirements */}
            {previewFiles.map(f => (
              <FileChangeAccordion
                key={f.filePath}
                filePath={f.filePath}
                diff={f.diff}
                additions={f.additions}
                deletions={f.deletions}
                defaultExpanded={false}
              />
            ))}

            {/* Agent fix items */}
            {agentChecks.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" className={classes.sectionLabel}>
                  Agent fixes (dispatched separately)
                </Typography>
                {agentChecks.map(c => (
                  <Box key={c.checkId} className={classes.agentItem}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="body2">
                        <strong>{c.checkName}</strong>
                      </Typography>
                      <Chip
                        icon={<SmartToyIcon style={{ fontSize: 12 }} />}
                        label="agent"
                        size="small"
                        className={classes.agentChip}
                      />
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      {c.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {applying && (
              <Box mt={2}>
                <LinearProgress />
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>
                  Creating PR and dispatching agent workflows…
                </Typography>
              </Box>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={onClose} disabled={applying}>
              Cancel
            </Button>
            <Button
              onClick={() => void runPreview()}
              disabled={previewLoading || applying}
              color="primary"
            >
              Refresh preview
            </Button>
            <Button
              onClick={() => void handleApply()}
              disabled={applying || previewLoading || !hasPreviewed}
              color="primary"
              variant="contained"
            >
              Open Pull Request{agentCount > 0 ? ' + dispatch agent' : ''}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
